// Which action a personal session is actually asking for.
//
// Three places used to answer this independently — the status badge, the
// «Продолжить» button and the destructive «Войти заново» button — from
// overlapping fields (a 60s heartbeat, a 1800s auth_request, a durable
// status). They drifted apart every time one of those fields changed
// meaning, and the operator was shown a session mid-authorization next to a
// button offering to wipe that authorization. One module, one answer.

export function normalizeAuthState(value) {
  return String(
    value || ''
  ).trim().toLowerCase();
}

export const WAITING_AUTH_STATES = [
  'wait_phone',
  'wait_code',
  'wait_password',
  'wait_email_code',
  'wait_device_confirmation',
  'wait_pairing_code',
  'wait_qr',
];

export function sessionAuthIsUnrecoverable(session) {
  // Set only by the worker's crash-escalation path, after a streak of failed
  // restarts. worker_status "error" cannot stand in for it: publishEvent
  // ("error") fires for something as ordinary as a mistyped SMS code, and
  // offering to wipe the authorization there discards a live auth flow.
  if (session?.needs_reauth) {
    return true;
  }

  // A messenger-side ban never heals by retrying.
  if (
    String(session?.worker_status || '').toLowerCase() === 'banned'
  ) {
    return true;
  }

  // The worker gave up on the auth flow, or the app stored an escalated
  // crash streak as a failed authorization. Either way retrying the same
  // credentials cannot help, and the stale auth_request left behind must not
  // keep offering «Продолжить».
  //
  // Gated on the worker not being connected. auth_state is written only by
  // worker events, which travel over fire-and-forget pub/sub and are dropped
  // on a lease mismatch — so a session that recovered while the subscriber
  // was down keeps a stale FAILED forever. A live "connected" heartbeat is
  // proof the authorization works, and it must outrank the stored value
  // rather than leaving a button that wipes a working login.
  if (
    normalizeAuthState(session?.auth_state) === 'failed'
    && String(session?.worker_status || '').toLowerCase() !== 'connected'
  ) {
    return true;
  }

  // No heartbeat at all plus a durable BAN: the worker is gone and the
  // stored state is the only truth left.
  //
  // Deliberately not 'error'. The durable status is written from the same
  // event for every escalation class, including the ones explicitly marked
  // reauthRequired: false — so a network outage or a missing env var landed
  // here the moment the crash heartbeat expired, and the row offered to wipe
  // the authorization beside a last_error reading "авторизация, скорее
  // всего, в порядке". On WhatsApp that button deletes the Baileys
  // credentials outright. A real credential escalation is already covered
  // above by needs_reauth and by the durable auth_state FAILED.
  return (
    !session?.worker_status
    && String(session?.status || '').toLowerCase() === 'banned'
  );
}

export function sessionNeedsAuthAttention(session) {
  // An unrecoverable session is authoritative: the auth flow is over,
  // whatever a stale auth_request still claims (it lives 1800s, the
  // heartbeat 60s). Without this, a crash mid-authorization showed
  // «Продолжить» — which posts to a dead worker — beside «Войти заново».
  if (sessionAuthIsUnrecoverable(session)) {
    return false;
  }

  // A stopped or disconnected worker is nobody to talk to. auth_request
  // lives 1800s against a 60s heartbeat, so it long outlasts «Отключить» —
  // and the row offered «Продолжить» beside «Подключить» for the next half
  // hour, one opening a form no process was listening to.
  const stoppedOnPurpose = (
    String(session?.worker_status || '').toLowerCase() === 'disconnected'
    // The endpoint writes the DURABLE status and deletes the heartbeat
    // keys — it never writes that heartbeat status — so keying on
    // worker_status alone meant this never fired for the «Отключить» it
    // names, and the row showed «Продолжить», «Подключить» and "Введите
    // код" together for the next half hour.
    || (
      !session?.worker_status
      && String(session?.status || '').toLowerCase() === 'disconnected'
    )
  );

  if (stoppedOnPurpose) {
    return false;
  }

  // Neither is a worker counting down to its own restart. The auth code
  // would be typed into a process that exits before reading it, and the
  // pub/sub carrying it has no replay — the operator would see nothing
  // happen and no reason why.
  if (session?.restarting) {
    return false;
  }

  // Otherwise auth_request outranks the heartbeat: a live worker sets it
  // when it is actually blocked on input, and checking the heartbeat first
  // stranded an operator mid-SMS whenever it lapsed for a moment.
  if (session?.auth_request) {
    return true;
  }

  // Falling back to the durable auth_state, though, requires a live worker:
  // without one that state describes a conversation nobody is having, and
  // «Продолжить» would open a form no process is listening to.
  if (!session?.worker_status) {
    return false;
  }

  return WAITING_AUTH_STATES.includes(
    normalizeAuthState(session?.auth_state)
  );
}

// The destructive action, named at the call site for what it offers. It is
// the same question as sessionAuthIsUnrecoverable on purpose: the moment the
// two could answer differently is the moment the panel shows «Продолжить»
// and «Войти заново» side by side again.
export function sessionCanReauth(session) {
  return sessionAuthIsUnrecoverable(session);
}

// What the status badge is saying, as a value rather than as JSX.
//
// Extracted so it can be cross-checked against the buttons: a test that only
// compared the two button predicates was tautological — the continue button
// returns false whenever the reauth button returns true, by construction —
// and it never touched the badge, which is where the contradictions were
// actually visible to the operator.
export function sessionBadgeKind(session) {
  if (sessionAuthIsUnrecoverable(session)) {
    const banned = [
      String(session?.status || '').toLowerCase(),
      String(session?.worker_status || '').toLowerCase(),
    ].includes('banned');

    return banned ? 'banned' : 'needs-reauth';
  }

  // A restart countdown is the more specific fact, and it outranks the
  // stored auth_state. routes.py writes AUTHORIZING on every force restart
  // and a worker that never connects never overwrites it, so a crash-looping
  // session announced "Идёт авторизация — действий не требуется"
  // permanently, immediately after the operator pressed «Перезапустить».
  if (session?.restarting) {
    return 'restarting';
  }

  // Progress, not a request for input. The app sets this state on every
  // start and restart, so it is what the row shows right after «Подключить».
  //
  // Only while a worker is actually running: "disconnected" is a heartbeat
  // like any other, so the row of a session the operator had just stopped
  // said "Идёт авторизация — действий не требуется" beside a «Подключить»
  // button.
  if (
    !session?.auth_request
    && !sessionCanConnect(session)
    && normalizeAuthState(session?.auth_state) === 'authorizing'
  ) {
    return 'authorizing';
  }

  // The badge may only ask for input where the input can actually go —
  // the same condition the «Продолжить» button uses.
  if (sessionNeedsAuthAttention(session)) {
    return 'needs-input';
  }

  if (!session?.worker_status) {
    return 'stopped';
  }

  return 'worker-status';
}

// Whether the row should offer to start the worker. Kept here with the rest:
// this branch lived on in the component reading raw worker_status, so a
// session with no heartbeat and a stale auth_request rendered «Продолжить»,
// «Подключить» and a badge saying "Введите код" all at once.
export function sessionCanConnect(session) {
  // A worker in its restart backoff is still running: «Подключить» would
  // answer already_running and change nothing.
  if (session?.restarting) {
    return false;
  }

  const workerStatus = String(session?.worker_status || '').toLowerCase();

  return !workerStatus || workerStatus === 'disconnected';
}
